import type { ModalState } from '@/atoms'
import { useCloseModal, useCurrentModal } from '@/atoms'
import { DraggableLayer } from '@/components/DraggableLayer'
import { Backdrop, Box, Dialog, Stack } from '@mui/material'
import {
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ModalBody } from './elements/ModalBody'
import { DragHandle, StyledModalContainer } from './hooks/useStyles'

interface Props {
  scrollbars?: boolean
  header?: ReactElement
  footer?: ReactElement
  config?: Partial<ModalState['config']>
  children?: ReactNode
  adornment?: ReactNode
  minWidth?: number
  maxWidth?: number
  testid?: string
  customCloseModal?: () => void
  style?: CSSProperties
}

export function ModalContent(props: Props) {
  const {
    children,
    header,
    footer,
    config: customConfig,
    maxWidth,
    minWidth,
    testid = 'modal',
    adornment,
    style,
    customCloseModal,
  } = props

  const ref = useRef<null | HTMLDivElement>(null)
  const defaultCloseModal = useCloseModal()
  const { config } = useCurrentModal()

  const closeModal = customCloseModal ?? defaultCloseModal

  const { maxHeight, disableEscapeKeyDown, disableBackdropClick } = useMemo(
    () => ({
      ...config,
      ...(customConfig ?? {}),
    }),
    [config, customConfig],
  )

  const [innerHeight, setInnerHeight] = useState<number>(Number.MAX_SAFE_INTEGER)

  const scrollbarsHeight = useMemo(
    () => Math.ceil(Math.min(Math.abs(maxHeight), innerHeight)),
    [maxHeight, innerHeight],
  )

  const onClose = useCallback(
    (_event, reason) => {
      if (reason === 'backdropClick' && disableBackdropClick) return
      closeModal()
    },
    [closeModal, disableBackdropClick],
  )

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      slots={{
        backdrop: Backdrop,
      }}
      disableEscapeKeyDown={disableEscapeKeyDown}
      transitionDuration={600}
      PaperProps={{
        ref,
      }}
    >
      <StyledModalContainer data-testid={testid} minWidth={minWidth} maxWidth={maxWidth}>
        <DraggableLayer
          draggable
          container={ref.current}
          render={(onMouseDown, dragging) => (
            <Stack direction="row" width="100%" height="100%">
              <Stack direction="column" height="100%" width="100%" flexShrink={1}>
                {!!header && (
                  <DragHandle dragging={dragging} onMouseDown={onMouseDown}>
                    {header}
                  </DragHandle>
                )}

                <Box width="100%" height="100%" flex="0 1 auto">
                  <ModalBody
                    height={scrollbarsHeight}
                    onUpdateHeight={setInnerHeight}
                    scrollBars={props.scrollbars}
                    style={style}
                  >
                    {children}
                  </ModalBody>
                </Box>

                {!!footer && footer}
              </Stack>
              {adornment}
            </Stack>
          )}
        />
      </StyledModalContainer>
    </Dialog>
  )
}
